import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextField,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import "../../styles/inputBox.css";
import CloseIcon from "@mui/icons-material/Close";
import uploadFileIcon from "../../assets/images/uploadFileIcon.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { currencies } from "../../constant/VonqConstant";
import { MenuItem, Select } from "@mui/material";
import ProfileStepDots from "./ProfileStepDots";
import PrimaryColorPicker from "./PrimaryColorPicker";
import {
  DEFAULT_PRIMARY_COLOR,
  isValidHexColor,
  normalizeHexColor,
} from "../../utils/colorUtils";
import {
  formatPostcodeInput,
  getPostcodeError,
  getPostcodePlaceholder,
  isValidPostcode,
  normalizePostcode,
} from "../../utils/postcodeUtils";

interface ProfileProps {
  onNext: () => void;
  setProfileData: (data: any) => void;
  initialData: any;
  mode: any;
  currentPage?: number;
  onGoToPage?: (page: number) => void;
}

const ProfileFirst: React.FC<ProfileProps> = ({
  onNext,
  initialData,
  setProfileData,
  mode,
  currentPage = 1,
  onGoToPage,
}: ProfileProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const { id } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputId = "company-logo-upload-input";
  // handle Form field
  const [logo, setLogo] = useState<File | string | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>(DEFAULT_PRIMARY_COLOR);
  const [webUrl, setWebUrl] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("");

  const [errors, setErrors] = useState({
    logo: "",
    primaryColor: "",
    webUrl: "",
    name: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
  });

  const formIsValid = () => {
    const newErrors = {
      logo: !logo ? "Logo is required" : "",
      primaryColor: !isValidHexColor(primaryColor)
        ? "Please select a valid colour"
        : "",
      webUrl: !webUrl?.trim() ? "WebUrl is required" : "",
      name: !name?.trim() ? "Comapnay Name is required" : "",
      city: !city?.trim() ? "City Name is required" : "",
      state: !state?.trim() ? "State Name is required" : "",
      country: !country?.trim() ? "Country Name is required" : "",
      zipCode: !zipCode?.trim()
        ? "Postcode is required"
        : !isValidPostcode(zipCode, country)
          ? getPostcodeError(country)
          : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  //params
  const params = useParams();
  const navigate = useNavigate();

  const buildStepData = () => ({
    logo,
    primaryColor,
    webUrl,
    name,
    city,
    state,
    country,
    zipCode,
    addressLine1,
    defaultCurrency: defaultCurrency || null,
  });

  const syncToParent = () => {
    setProfileData({ ...initialData, ...buildStepData() });
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!formIsValid()) return;

    syncToParent();
    onNext();
  };

  const handleStepNavigate = (page: number) => {
    syncToParent();
    onGoToPage?.(page);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0];

        // Check file size (4MB = 4 * 1024 * 1024 bytes)
        const maxSize = 4 * 1024 * 1024; // 4MB in bytes
        if (selectedFile.size > maxSize) {
          setLogo(null);
          setErrors((prevErrors) => ({
            ...prevErrors,
            logo: "File size must be less than 4MB. Please choose a smaller file.",
          }));
          // Reset input so selecting the same file again retriggers change
          event.target.value = "";
          return;
        }

        const isSvg =
          selectedFile.type === "image/svg+xml" ||
          /\.svg$/i.test(selectedFile.name);
        if (isSvg) {
          setLogo(null);
          setErrors((prevErrors) => ({
            ...prevErrors,
            logo: "SVG files are not allowed. Please upload PNG, JPG, or WEBP.",
          }));
          // Reset input so selecting the same file again retriggers change
          event.target.value = "";
          return;
        }

        setLogo(selectedFile);
        setErrors((prevErrors) => ({
          ...prevErrors,
          logo: "",
        }));
      }
    }
  };

  const handleClearImage = () => {
    setLogo(null);
  };

  const goBack = () => {
    navigate("/");
  };

  useEffect(() => {
    if (initialData) {
      setName(initialData?.name);
      setCity(initialData?.city);
      setState(initialData?.state);
      setCountry(initialData?.country);
      setZipCode(
        initialData?.zipCode
          ? normalizePostcode(initialData.zipCode, initialData?.country)
          : ""
      );
      setAddressLine1(initialData?.addressLine1 || "");
      setDefaultCurrency(initialData?.defaultCurrency || "");
      setLogo(initialData?.logo);
      setPrimaryColor(
        initialData?.primaryColor
          ? normalizeHexColor(initialData.primaryColor)
          : DEFAULT_PRIMARY_COLOR
      );
      setWebUrl(initialData?.webUrl);
    }
  }, []);

  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;

    const nextData = { ...initialData, ...buildStepData() };
    const hasChanges =
      JSON.stringify(nextData) !== JSON.stringify(initialData);

    if (hasChanges) {
      setProfileData(nextData);
    }
  }, [
    logo,
    primaryColor,
    webUrl,
    name,
    city,
    state,
    country,
    zipCode,
    addressLine1,
    defaultCurrency,
    initialData,
    setProfileData,
  ]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleColorSelect = (color: string) => {
    setPrimaryColor(color);
    if (isValidHexColor(color)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        primaryColor: "",
      }));
    }
  };

  return (
    <div>
      <Container
        maxWidth="md"
        style={{
          display: "flex",
          padding: "20px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="784px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid item display="flex" alignItems="center" gap="10px">
                {id && (
                  <ArrowBackIcon
                    onClick={goBack}
                    style={{ cursor: "pointer" }}
                  />
                )}

                <Typography
                  className="profile-title"
                  color="#273848"
                  fontWeight={700}
                  fontSize="20.94px"
                >
                  {id ? "Update Profile" : "Create Profile"}
                </Typography>
              </Grid>
              <ProfileStepDots
                currentPage={currentPage}
                onGoToPage={handleStepNavigate}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Company Name<span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 11px", // Add padding here
                  },
                }}
                placeholder="Company Name"
                fullWidth
                className="input-box-custom"
                error={!!errors.name}
                helperText={errors.name}
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  setName(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      name: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Logo <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <input
                ref={fileInputRef}
                id={logoInputId}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleImageChange}
              />
              <Box
                component={logo ? "div" : "label"}
                htmlFor={logo ? undefined : logoInputId}
                sx={{
                  border: "2px dashed #D5D5D5",
                  borderRadius: "20px",
                  padding: logo ? 0 : isMobile ? "30px" : "48px",
                  cursor: "pointer",
                  position: "relative",
                  display: "block",
                  transition: "all 0.3s ease",
                  background: logo
                    ? "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
                    : "#ffffff",
                  "&:hover": {
                    borderColor: "#84E1CC",
                    borderStyle: "solid",
                    boxShadow: "0 8px 16px rgba(132, 225, 204, 0.2)",
                  },
                }}
              >
                {logo ? (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 3,
                      minHeight: "200px",
                      "&:hover .delete-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <img
                      src={
                        typeof logo === "string"
                          ? logo
                          : URL.createObjectURL(logo)
                      }
                      alt="selected-image"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        borderRadius: "12px",
                        objectFit: "contain",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      }}
                    />

                    {/* Modern Delete Overlay */}
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearImage();
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.1) rotate(90deg)",
                            boxShadow: "0 6px 16px rgba(239, 68, 68, 0.6)",
                          },
                        }}
                      >
                        <CloseIcon
                          sx={{
                            color: "#ffffff",
                            fontSize: 24,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Change Image Button */}
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFilePicker();
                        }}
                        sx={{
                          background:
                            "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                          color: "#fff",
                          borderRadius: "12px",
                          padding: "10px 24px",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: "0 4px 12px rgba(132, 225, 204, 0.4)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(132, 225, 204, 0.6)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        Change Image
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 2,
                    }}
                  >
                    <img src={uploadFileIcon} alt="uploadFile-Icon" />
                    <Grid my={1}></Grid>
                    <Typography align="center" color="#4E4E4E">
                      Drag a file or{" "}
                      <Box
                        component="span"
                        color="#84E1CC"
                        sx={{
                          textDecoration: "underline",
                          textDecorationColor: "#84E1CC",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        select
                      </Box>
                    </Typography>
                  </Box>
                )}
              </Box>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.logo}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <PrimaryColorPicker
                value={primaryColor}
                onChange={handleColorSelect}
                error={errors.primaryColor}
                onBlur={() => {
                  if (!isValidHexColor(primaryColor)) {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      primaryColor: "Enter a valid hex colour (e.g. #244565)",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Website URL<span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 11px", // Add padding here
                  },
                }}
                placeholder="Website URL"
                fullWidth
                className="input-box-custom"
                error={!!errors.webUrl}
                helperText={errors.webUrl}
                value={webUrl}
                onChange={(e) => {
                  const value = e.target.value;
                  setWebUrl(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      webUrl: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: "15px" }}>
              <Typography className="input-label-custom">
                Street address (for campaigns)
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 11px",
                  },
                }}
                placeholder="Office street address"
                fullWidth
                className="input-box-custom"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "15px",
              }}
            >
              <Grid item style={{ width: "100%", marginLeft: "2px" }}>
                <Typography className="input-label-custom">
                  City <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <TextField
                  sx={{
                    // mt: 1,
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 11px", // Add padding here
                    },
                  }}
                  placeholder="Enter City Name"
                  fullWidth
                  className="input-box-custom"
                  value={city}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCity(value);
                    if (value.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        city: "",
                      }));
                    }
                  }}
                />
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.city}
                </InputLabel>
              </Grid>
              <Grid item style={{ width: "100%", marginLeft: "5px" }}>
                <Typography className="input-label-custom">
                  State <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <TextField
                  sx={{
                    // mt: 1,
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 11px", // Add padding here
                    },
                  }}
                  name="workHours"
                  placeholder="Enter State Name"
                  fullWidth
                  className="input-box-custom"
                  value={state}
                  onChange={(e) => {
                    const value = e.target.value;
                    setState(value);
                    if (value.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        state: "",
                      }));
                    }
                  }}
                />
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.state}
                </InputLabel>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "15px",
              }}
            >
              <Grid item style={{ width: "100%", marginLeft: "2px" }}>
                <Typography className="input-label-custom">
                  Country <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <TextField
                  sx={{
                    // mt: 1,
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 11px", // Add padding here
                    },
                  }}
                  placeholder="Enter Country Name"
                  fullWidth
                  className="input-box-custom"
                  value={country}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCountry(value);
                    if (value.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        country: "",
                      }));
                    }
                  }}
                />
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.country}
                </InputLabel>
              </Grid>
              <Grid item style={{ width: "100%", marginLeft: "5px" }}>
                <Typography className="input-label-custom">
                  Postcode <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <TextField
                  sx={{
                    // mt: 1,
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 11px", // Add padding here
                    },
                  }}
                  name="zipCode"
                  placeholder={getPostcodePlaceholder(country)}
                  fullWidth
                  className="input-box-custom"
                  value={zipCode}
                  onChange={(e) => {
                    const formatted = formatPostcodeInput(
                      e.target.value,
                      country
                    );
                    setZipCode(formatted);

                    if (!formatted.trim()) {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        zipCode: "Postcode is required.",
                      }));
                    } else if (isValidPostcode(formatted, country)) {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        zipCode: "",
                      }));
                    } else {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        zipCode: "",
                      }));
                    }
                  }}
                  onBlur={() => {
                    if (!zipCode.trim()) return;

                    if (isValidPostcode(zipCode, country)) {
                      setZipCode(normalizePostcode(zipCode, country));
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        zipCode: "",
                      }));
                    } else {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        zipCode: getPostcodeError(country),
                      }));
                    }
                  }}
                  inputProps={{
                    maxLength: 12,
                  }}
                />
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.zipCode}
                </InputLabel>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} style={{ marginTop: "15px" }}>
              <Typography className="input-label-custom">
                Default currency (for campaigns)
              </Typography>
              <Grid mb={1}></Grid>
              <Select
                fullWidth
                displayEmpty
                value={defaultCurrency}
                onChange={(e) => setDefaultCurrency(e.target.value)}
                renderValue={(selected) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#999" }}>Select currency (optional)</span>
                  )
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid mt="35px"></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid></Grid>
              <Grid
                item
                xs={isMobile ? 0 : 3}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ProfileFirst;
